import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, ChevronDown, LayoutDashboard, Shield } from 'lucide-react';
import { useNavbarVisibility } from '@/context/NavbarVisibilityContext';
import { useTheme } from 'next-themes';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useAuth } from '../../hooks/authentication/useAuth';
import { usePermissions, PERMISSIONS } from '../../hooks/authentication/usePermissions';

const Navbar = () => {
  const { theme } = useTheme();
  const [showSolutions, setShowSolutions] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavbarVisible } = useNavbarVisibility();
  const { isAuthenticated, user, logout, login, isLoading, msalReady } = useAuth();
  const { hasPermission, role } = usePermissions();

  // Check permissions for dashboard access
  const canAccessBusinessDashboard = hasPermission(PERMISSIONS.VIEW_CAMPAIGN_MANAGEMENT_DASHBOARD);
  const canAccessAdminDashboard = hasPermission(PERMISSIONS.MANAGE_USERS);

  // Handle login click - uses redirect flow (same tab)
  const handleLogin = async () => {
    try {
      await login();
      // Page will redirect to Microsoft login
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  // Handle logout click - uses redirect flow (same tab)
  const handleLogout = async () => {
    setShowUserMenu(false);
    setIsOpen(false);
    await logout();
    // Page will redirect after logout
  };

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };


  // Hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const isVisible = prevScrollPos > currentScrollPos || currentScrollPos < 10;
      setVisible(isVisible);
      setNavbarVisible(isVisible);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos, setNavbarVisible]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSolutions(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setShowSolutions(false);
    setShowUserMenu(false);
    setIsOpen(false);
  }, [location]);

  const navbarClasses = `bg-foreground text-background fixed w-full h-auto transition-transform duration-300 z-10 ${
    visible ? 'translate-y-0' : '-translate-y-full'
  }`;

  return (
    <nav className={navbarClasses}>
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="text-xl font-bold">
              {theme === 'dark' ? (
                <img src="/logo light.png" className="w-60 h-auto"/>
              ) : (
                <img src="/logo dark.png" className="w-60 h-auto"/>
              )}
            </Link>
          </div>
          {/* <ThemeToggle/>     */}

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            <Link to="/" className="inline-flex justify-center rounded-md px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800 focus:outline-none">
              Home
            </Link>

            {/* Solutions Dropdown - Only show if authenticated */}
            
            
            {/* Authentication Section */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 bg-yellow-500 text-black text-sm font-medium hover:bg-yellow-600 focus:outline-none transition-colors"
                >
                  <User size={18} />
                  <span className="max-w-[120px] truncate">
                    {user?.name || user?.email || 'User'}
                  </span>
                  <ChevronDown size={16} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {user?.email || ''}
                        </p>
                        {role && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {role.replace('_', ' ')}
                          </p>
                        )}
                      </div>
                      
                      {/* Business Dashboard - visible for users with VIEW_CAMPAIGN_MANAGEMENT_DASHBOARD permission */}
                      {canAccessBusinessDashboard && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/business-dashboard');
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          <LayoutDashboard size={16} />
                          Business Dashboard
                        </button>
                      )}
                      
                      {/* Admin Dashboard - visible for users with MANAGE_USERS permission */}
                      {canAccessAdminDashboard && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/admin-dashboard');
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <Shield size={16} />
                          Admin Dashboard
                        </button>
                      )}
                      
                      {(canAccessBusinessDashboard || canAccessAdminDashboard) && (
                        <div className="border-t border-gray-100 my-1"></div>
                      )}
                      
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={16} />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                disabled={isLoading || !msalReady}
                className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 bg-yellow-500 text-black text-sm font-medium hover:bg-yellow-600 focus:outline-none transition-colors disabled:opacity-50"
              >
                {isLoading || !msalReady ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <User size={18} />
                    Sign In
                  </>
                )}
              </button>
            )}
            
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md hover:text-gray-600 focus:outline-none"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-background backdrop-blur-md">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link to="/" className="block px-3 py-2 text-base font-medium hover:text-gray-600">
              Home
            </Link>
            
            {/* Mobile Auth Section */}
            {isAuthenticated ? (
              <>
                <div className="px-3 py-2 border-t border-gray-200 mt-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {user?.email || ''}
                  </p>
                  {role && (
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      {role.replace('_', ' ')}
                    </p>
                  )}
                </div>
                
                {/* Business Dashboard - mobile */}
                {canAccessBusinessDashboard && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/business-dashboard');
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-base font-medium text-blue-600 hover:bg-blue-50"
                  >
                    <LayoutDashboard size={18} />
                    Business Dashboard
                  </button>
                )}
                
                {/* Admin Dashboard - mobile */}
                {canAccessAdminDashboard && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/admin-dashboard');
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-base font-medium text-purple-600 hover:bg-purple-50"
                  >
                    <Shield size={18} />
                    Admin Dashboard
                  </button>
                )}
                
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut size={18} />
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setIsOpen(false);
                  handleLogin();
                }}
                disabled={isLoading || !msalReady}
                className="flex items-center gap-2 w-full px-3 py-2 text-base font-medium text-yellow-600 hover:bg-yellow-50"
              >
                <User size={18} />
                {isLoading || !msalReady ? 'Loading...' : 'Sign In'}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
